import { EditOutlined, FactCheckOutlined, MoreTimeOutlined, OpenInNewOutlined, ThumbDownOutlined } from "@mui/icons-material";
import PhoneCallbackOutlinedIcon from "@mui/icons-material/PhoneCallbackOutlined";
import { Box, Button } from "@mui/material";
import { isFuture } from "date-fns";
import isPast from "date-fns/isPast";
import { useConfirm } from "material-ui-confirm";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import IconGroup from "../../../../design/icon-group";
import {
	requestShiftAction,
	resetWorkerRequestMetaData,
	setShiftFormData,
	setWorkerRequestMetaData,
} from "../../store/worker-requests-slice";

interface formDataTypes {
	requestId: number;
	ftShiftId: number;
	comment: string;
}
interface ActionsProps {
	openCreateNewShiftDialog: (date: string) => void;
	date: string;
	formData: formDataTypes;
	requestData: {
		name: string;
		requestType: string;
		// leaveType: string;
		startDateTime: string;
		endDateTime: string;
		requestStatusCode: string;
		requestSubStatusCode: string;
		flexShiftIds: string;
	};
	postCoverageForAutoApprove?: boolean;
	statusCode: string;
	openShiftDetailsDialog: (flexShiftIds: string) => void;
  id: string;
  canEdit: boolean;
  handleEditClick: () => void;
  canAcceptReject: boolean;
  canViewCallDetail: boolean;
  handleViewCallDetail: () => void;
}

export default function Actions({
	openCreateNewShiftDialog,
	date,
	formData,
	requestData,
	postCoverageForAutoApprove = false,
	statusCode,
	openShiftDetailsDialog,
  id,
  canEdit,
  handleEditClick,
  canAcceptReject,
  canViewCallDetail,
  handleViewCallDetail,
}: ActionsProps) {
	const dispatch = useDispatch();
	const confirm = useConfirm();
	const { t } = useTranslation();

  const editIcon = {
    icon: <EditOutlined id={`edit-request-${id}`} sx={{ fontSize: "16px" }} />,
    tooltip: t("common_edit"),
    onClick: (e: React.MouseEvent) => {
      handleEditClick();
      e.stopPropagation();
    },
  };

  const callIcon = {
    icon: (
      <PhoneCallbackOutlinedIcon
        id={`listen-call-${id}`}
        sx={{ fontSize: "16px" }}
      />
    ),
    tooltip: t("worker-requests_listen-and-read-transcript"),
    onClick: (e: React.MouseEvent) => {
      handleViewCallDetail();
      e.stopPropagation();
    },
  };

  const handleApproveClick = async () => {
		if (requestData?.requestStatusCode === "p" && requestData?.requestSubStatusCode === "pl") {
			try {
				await confirm({
					title: t("worker-requests_absence-limit-title"),
					description: t("worker-requests_absence-limit-desc"),
					confirmationText: t("worker-requests_proceed"),
					cancellationText: t("common_cancel"),
					confirmationButtonProps: { variant: "outlined", color: "error" },
				});
			} catch {
				// User canceled the confirmation
				return;
			}
		}

		// Proceed with approval
		dispatch(resetWorkerRequestMetaData());
		dispatch(setShiftFormData({ ...formData, decisionCode: "a" }));
		dispatch(setWorkerRequestMetaData(requestData));
		openCreateNewShiftDialog(date);
	};

	const handleRejectClick = async () => {
		if (isFuture(new Date(requestData.startDateTime)) && requestData.flexShiftIds) {
			try {
				await confirm({
					title: t("worker-requests_decline-title"),
					description: t("worker-requests_decline-coverage-desc"),
					confirmationText: t("worker-requests_decline"),
					cancellationText: t("common_cancel"),
					confirmationButtonProps: { variant: "outlined", color: "error" },
				});
			} catch {
				// User canceled the confirmation
				return;
			}
		} else if (isFuture(new Date(requestData.startDateTime)) && (statusCode === "a" || statusCode === "aa")) {
			// For future approved requests without coverage, open the dialog to Post Coverage or Decline
			dispatch(resetWorkerRequestMetaData());
			dispatch(setShiftFormData({ ...formData, decisionCode: "a" }));
			dispatch(setWorkerRequestMetaData(requestData));
			openCreateNewShiftDialog(date);
			return;
		} else {
			try {
				await confirm({
					title: t("worker-requests_decline-title"),
					description: t("worker-requests_decline-confirm-desc"),
					confirmationText: t("worker-requests_decline"),
					cancellationText: t("common_cancel"),
					confirmationButtonProps: { variant: "outlined", color: "error" },
				});
			} catch {
				// User canceled the confirmation
				return;
			}
		}

		// Proceed with decline
		dispatch(resetWorkerRequestMetaData());
		const payload = {
			...formData,
			decisionCode: "r",
			flexShiftDetails: null,
		};
		dispatch(requestShiftAction(payload));
	};

  const ActionWrapper = ({ icons }: { icons: any[] }) => (
    <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <IconGroup
        icons={[
          ...icons,
          ...(canViewCallDetail ? [callIcon] : []),
          ...(canEdit ? [editIcon] : []),
        ]}
      />
    </Box>
  );

  // Users without accept/reject permission (e.g. ReadOnly) still get the
  // action column so they can access call recordings & transcripts, but none
  // of the status-based approve/decline actions.
  if (!canAcceptReject) {
    return <ActionWrapper icons={[]} />;
  }

	if( (statusCode === "a" || statusCode === "aa") && requestData?.flexShiftIds){
		return (
      <ActionWrapper
        icons={[
          {
            icon: (
              <OpenInNewOutlined
                id={`view-details-${id}`}
                sx={{
                  fontSize: "16px",
                }}
              />
            ),
            tooltip: t("worker-requests_view-details"),
            onClick: (e: React.MouseEvent) => {
              openShiftDetailsDialog(requestData.flexShiftIds);
              e.stopPropagation();
            },
          },
          {
            icon: (
              <ThumbDownOutlined
                id={`decline-request-${id}`}
                sx={{
                  fontSize: "16px",
                }}
              />
            ),
            tooltip: t("worker-requests_decline"),
            onClick: (e: React.MouseEvent) => handleRejectClick(),
          },
        ]}
      />
    );
	}
	if (statusCode === "aa" || statusCode === "a") {
		return (
      <ActionWrapper
        icons={
          isPast(new Date(requestData.startDateTime))
            ? [
                {
                  icon: (
                    <ThumbDownOutlined
                      id={`decline-request-${id}`}
                      sx={{
                        fontSize: "16px",
                      }}
                    />
                  ),
                  tooltip: t("worker-requests_decline"),
                  onClick: (e: React.MouseEvent) => handleRejectClick(),
                },
              ]
            : [
                {
                  icon: (
                    <FactCheckOutlined
                      id={`decline-request-${id}`}
                      sx={{
                        fontSize: "16px",
                      }}
                    />
                  ),
                  tooltip: t("worker-requests_post-coverage-or-decline"),
                  onClick: (e: React.MouseEvent) => handleRejectClick(),
                },
              ]
        }
      />
    );
	}
	if (statusCode === "r") {
		return (
      <ActionWrapper
        icons={[
          {
            icon: (
              <FactCheckOutlined
                id={`approve-request-${id}`}
                sx={{
                  fontSize: "16px",
                }}
              />
            ),
            tooltip: t("worker-requests_review-and-approve"),
            onClick: (e: React.MouseEvent) => {
              handleApproveClick();
              e.stopPropagation();
            },
          },
        ]}
      />
    );
	}
  if (statusCode === "p") {
    return (
      <ActionWrapper
        icons={[
          {
            icon: (
              <FactCheckOutlined
                id={`review-request-${id}`}
                sx={{
                  fontSize: "16px",
                }}
              />
            ),
            tooltip: t("worker-requests_review"),
            onClick: (e: React.MouseEvent) => {
              handleApproveClick();
              e.stopPropagation();
            },
          },
        ]}
      />
    );
  }
	return (
		<></>
	)
}
